import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, Position } from '@xyflow/react';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as ProfitCenterIcon } from 'assets/icons/profitCenter.svg';
import { clsx } from 'clsx';
import { VIRTUAL_PROPERTY_STATUS } from 'constants/general';

import IconButton from '../../../../../../components/IconButton/IconButton';
import SmallLoader from '../../../../../../components/SmallLoader/SmallLoader';
import { formatRevenueAndPercentage } from '../../../../../../utils/helpers';
import CustomNodeItem from './CustomNodeItem';

import type { ICustomNode } from '../interfaces/ICustomNode';

import styles from './sass/CustomNode.module.scss';

function CustomNode({ data }: ICustomNode) {
  const { t } = useTranslation();
  const { isDisabled } = data;
  const [isLoading, setIsLoading] = useState(true);
  const sumCalcValue = (Number(data?.virtualProperties?.assetValue?.[data.uniqueIdentifier]?.split(' ')[0]) || 0)
    + (Number(data?.virtualProperties?.implicitAssetValue?.[data.uniqueIdentifier]?.split(' ')[0]) || 0);

  useEffect(() => {
    const isLoadingObject = Object.values(data?.virtualPropertyStatuses || {})
      .some((status) => status?.status === VIRTUAL_PROPERTY_STATUS.CALCULATING);
    setIsLoading(isLoadingObject);
  }, [data?.virtualPropertyStatuses]);

  return (
    <div className={clsx(styles.customNode, {
      [styles.dragTarget]: data.isDragTarget === true,
    })}
    >
      <div className={clsx(styles.header, styles[data?.ownership?.replace(/\s+/g, '')], {
        [styles.disabled]: isDisabled,
      })}
      >
        <div className={styles.titleWrapper}>
          <ProfitCenterIcon width={18} height={18} />
          <p className={clsx({ [styles.disabled]: isDisabled })}>{data.name}</p>
        </div>
        <div className={styles.activeWrapper}>
          {isLoading ? <SmallLoader size={14} /> : null}
          {data?.latestStatus ? (
            <IconButton
              className={styles.info}
              icon={<InfoIcon />}
              onClick={() => data.onDisabledInfoPress(data)}
              isWarning
            />
          ) : null}
          {!data.isAccessDisabled ? (
            <input
              type="checkbox"
              id="isActiveProfitCenter"
              checked={!isDisabled}
              onChange={() => data.onActiveStatusCheck(data)}
              className={styles.checkbox}
            />
          ) : null}
        </div>
      </div>
      <div className={styles.content}>
        <CustomNodeItem
          title={t('valueChainMap.profitCenterId')}
          value={data.uniqueIdentifier || '-'}
          isDisabled={isDisabled}
        />
        <CustomNodeItem
          title={t('valueChainMap.relatedBalanceSheetAssets')}
          value={formatRevenueAndPercentage({
            actual: data?.netBookValue?.amount,
            currency: data?.netBookValue?.currency,
          })}
          calculateValue={formatRevenueAndPercentage({
            actual: sumCalcValue,
            currency: data?.netBookValue?.currency,
          })}
          isDisabled={isDisabled}
        />
        <CustomNodeItem
          title={t('valueChainMap.actualRevenue')}
          value={formatRevenueAndPercentage({
            actual: data?.totalRevenueRolling12MActual?.amount,
            currency: data?.totalRevenueRolling12MActual?.currency,
            percentage: `${data?.virtualProperties?.totalRevenuePercents || 0}`,
          })}
          isDisabled={isDisabled}
        />
        <CustomNodeItem
          title={t('valueChainMap.actualGrossMargin')}
          value={formatRevenueAndPercentage({
            actual: data?.totalGrossMarginRolling12MActual?.amount,
            currency: data?.totalGrossMarginRolling12MActual?.currency,
            percentage: `${data?.virtualProperties?.totalGrossProfitPercents || 0}`,
          })}
          isDisabled={isDisabled}
        />
        <CustomNodeItem
          title={t('valueChainMap.stock')}
          value={formatRevenueAndPercentage({
            actual: data?.wipFinishedGoodsStock?.amount,
            currency: data?.wipFinishedGoodsStock?.currency,
          })}
          isDisabled={isDisabled}
        />
        <Handle type="source" position={Position.Bottom} />
        <Handle type="target" position={Position.Top} />
      </div>
    </div>
  );
}

export default CustomNode;
